import type { ReactNode } from 'react';
import {
  Box,
  CircuitBoard,
  Cloud,
  Cpu,
  Gauge,
  HardDrive,
  Layers,
  MemoryStick,
  Network,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Config-driven spec-tile glyphs for the v2 asset detail Specs card
 * (asset-detail-visualizations design). Each spec_display_schema key maps to
 * an entry here; to add or change a spec's visual, edit SPEC_GLYPHS only —
 * the card renders whatever this registry says.
 *
 * Three tiers per entry, all optional:
 *   glyph — bespoke mini-SVG drawn next to the value (rack bars, bay
 *           squares, PCIe steps). Returning null falls back to `icon`.
 *   icon  — lucide icon next to the value.
 *   sub   — caption/gauge rendered under the value (e.g. PSU avg-draw bar).
 *
 * Values arrive as the formatted display string from renderSpecDisplay
 * ("800 W", "1U", "5") — glyph renderers parse the leading number and must
 * tolerate non-numeric values (return null → icon fallback).
 */

export interface SpecGlyphCtx {
  /** Latest daily avg power draw from telemetry, for the PSU gauge. */
  avgPowerDrawW: number | null;
}

export interface SpecGlyphEntry {
  /** spec_display_schema keys this entry serves. */
  keys: string[];
  glyph?: (value: string, ctx: SpecGlyphCtx) => ReactNode;
  icon?: LucideIcon;
  sub?: (value: string, ctx: SpecGlyphCtx) => ReactNode;
}

const PRIMARY = '#047857';
const MUTED = 'rgba(113, 113, 122, 0.35)';

function leadingNumber(value: string): number | null {
  const m = value.match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
}

/** Stacked rack bars — highlighted bars = rack units (1U/2U/4U…), capped at 3. */
function RackGlyph(value: string): ReactNode {
  const units = leadingNumber(value);
  if (units == null || units < 1) return null;
  const lit = Math.min(Math.round(units), 3);
  return (
    <svg width="28" height="20" viewBox="0 0 28 20" aria-hidden>
      {[0, 1, 2].map((i) => (
        <rect key={i} x="1" y={1 + i * 7} width="26" height="4" rx="1" fill={i < lit ? PRIMARY : MUTED} />
      ))}
    </svg>
  );
}

/** Filled squares — one per bay/socket, capped at 6. */
function BaysGlyph(value: string): ReactNode {
  const n = leadingNumber(value);
  if (n == null || n < 1 || n > 24) return null;
  const shown = Math.min(Math.round(n), 6);
  const w = shown * 12 + 2;
  return (
    <svg width={w} height="16" viewBox={`0 0 ${w} 16`} aria-hidden>
      {Array.from({ length: shown }, (_, i) => (
        <rect
          key={i}
          x={1 + i * 12}
          y="2"
          width="10"
          height="12"
          rx="2"
          fill={`${PRIMARY}b3`}
          stroke={PRIMARY}
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}

/** Ascending generation steps — filled up to the value, out of 6. */
function GenStepsGlyph(value: string): ReactNode {
  const gen = leadingNumber(value);
  if (gen == null || gen < 1 || gen > 6) return null;
  return (
    <svg width="40" height="20" viewBox="0 0 40 20" aria-hidden>
      {Array.from({ length: 6 }, (_, i) => (
        <rect
          key={i}
          x={i * 7}
          y={15 - i * 3}
          width="5"
          height={5 + i * 3}
          rx="1"
          fill={i < gen ? PRIMARY : MUTED}
        />
      ))}
    </svg>
  );
}

/** PSU utilization gauge — avg telemetry draw as a share of PSU capacity. */
function PowerDrawSub(value: string, ctx: SpecGlyphCtx): ReactNode {
  const watts = leadingNumber(value);
  if (watts == null || watts <= 0 || ctx.avgPowerDrawW == null) return null;
  const pct = Math.min(100, Math.max(0, (ctx.avgPowerDrawW / watts) * 100));
  return (
    <>
      <div className="relative mt-2 h-[5px] rounded-full bg-zinc-200/60">
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${pct}%`, background: PRIMARY }}
        />
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        {Math.round(ctx.avgPowerDrawW)} W avg draw
      </div>
    </>
  );
}

export const SPEC_GLYPHS: SpecGlyphEntry[] = [
  // Chassis / rack
  { keys: ['rack_units'], glyph: RackGlyph, icon: Layers },
  { keys: ['form_factor'], glyph: RackGlyph, icon: CircuitBoard }, // "1U"→bars; "PCIe"/"SXM"→icon
  // Bays, slots, sockets
  { keys: ['max_gpu_count', 'gpu_count'], glyph: BaysGlyph, icon: Cpu },
  { keys: ['cpu_socket_count', 'cpu_max_sockets'], glyph: BaysGlyph, icon: Cpu },
  { keys: ['drive_bays', 'drive_count'], glyph: BaysGlyph, icon: HardDrive },
  // Generations
  { keys: ['pcie_gen'], glyph: GenStepsGlyph, icon: CircuitBoard },
  // Power
  { keys: ['power_supply_watts', 'power_watts'], icon: Zap, sub: PowerDrawSub },
  { keys: ['tdp_watts', 'max_power_kw'], icon: Zap },
  // Memory & caches
  {
    keys: [
      'vram_gb',
      'max_ram_gb',
      'memory_capacity_gb',
      'system_memory_gb',
      'total_gpu_memory_gb',
      'gpu_memory_per_gpu_gb',
      'l3_cache_mb',
      'memory_type',
      'memory_speed_mts',
    ],
    icon: MemoryStick,
  },
  // Bandwidth / throughput / clocks
  {
    keys: ['memory_bandwidth_gbps', 'fp16_tflops', 'base_clock_ghz', 'boost_clock_ghz'],
    icon: Gauge,
  },
  // Networking / interconnect
  {
    keys: [
      'port_count',
      'port_speed_gbps',
      'switching_capacity_tbps',
      'interconnect_type',
      'gpu_interconnect',
      'interface',
      'rdma_infiniband',
      'gpudirect_rdma',
    ],
    icon: Network,
  },
  // Silicon identity
  { keys: ['gpu_model', 'cpu_model', 'cpu_cores', 'cpu_threads', 'vcpu_count', 'cpu_socket'], icon: Cpu },
  { keys: ['architecture'], icon: Layers },
  { keys: ['drive_type', 'capacity_tb'], icon: HardDrive },
  // Cloud
  { keys: ['provider', 'cloud_family', 'series'], icon: Cloud },
];

const BY_KEY = new Map<string, SpecGlyphEntry>(
  SPEC_GLYPHS.flatMap((e) => e.keys.map((k) => [k, e] as const)),
);

const FALLBACK: SpecGlyphEntry = { keys: [], icon: Box };

export function specGlyphFor(key: string | undefined): SpecGlyphEntry {
  return (key && BY_KEY.get(key)) || FALLBACK;
}
